import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "../../services/supabaseClient";
import PrimaryButton from "../Button/PrimaryButton";
import PrimaryTextField from "../PrimaryTextField/PrimaryTextField";
import KalbuPasirinkimas from '../KalbuPasirinkimas';
import Logo from "../../assets/LOGO.png";
import { Validate } from "../../functionsIsmesti/validation";
import { toast } from 'react-toastify';
import { Box, Typography } from "@mui/material";

/**
 * OTP-based password reset component
 * Sends a 6-digit code instead of magic link to avoid Gmail token consumption
 */
const RequestPasswordResetOTP = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  
  // Component states
  const [step, setStep] = useState(1); // 1: email, 2: OTP verification
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  
  // Check if user is already logged in
  useEffect(() => {
    const checkSession = async () => {
      const { data } = await supabase.auth.getSession();
      if (data.session) {
        navigate('/');
      }
    };
    
    checkSession();
  }, [navigate]);
  
  // Validate email
  useEffect(() => {
    if (!email) {
      setIsEmailValid(false);
      return;
    }
    
    const isValid = Validate(email, 'email');
    setIsEmailValid(isValid);
  }, [email]);
  
  // Countdown timer
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);
  
  // Send OTP
  const handleSendOTP = async (e) => {
    if (e) e.preventDefault();
    
    if (isLoading || !isEmailValid) return;
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/NaujasSlaptazodis`,
      });
      
      if (error) throw error;
      
      setStep(2);
      setCountdown(60); // 60 seconds cooldown
      toast.success(t('otp_sent') || 'Patvirtinimo kodas išsiųstas!');
    } catch (error) {
      console.error("OTP sending error:", error);
      toast.error(error.message || t('klaida'));
    } finally {
      setIsLoading(false);
    }
  };
  
  // Verify OTP
  const handleVerifyOTP = async (e) => {
    if (e) e.preventDefault();
    
    if (isLoading || !otp || otp.length !== 6) return;
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.verifyOtp({
        email,
        token: otp,
        type: 'recovery'
      });
      
      if (error) throw error;
      
      // OTP verified successfully, redirect to password reset
      navigate('/NaujasSlaptazodis');
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error(t('invalid_otp') || 'Neteisingas kodas. Bandykite dar kartą.');
    } finally {
      setIsLoading(false);
    }
  };
  
  // Resend OTP
  const handleResendOTP = async () => {
    if (countdown > 0 || isLoading) return;
    
    setIsLoading(true);
    
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/NaujasSlaptazodis`,
      });
      
      if (error) throw error;
      
      setCountdown(60);
      setOtp('');
      toast.success(t('otp_resent') || 'Naujas kodas išsiųstas!');
    } catch (error) {
      console.error("OTP resend error:", error);
      toast.error(error.message || t('klaida'));
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleGoToLogin = () => {
    navigate('/');
  };
  
  return (
    <div className="prisijungimoLangas">
      <div className="prisijungimoKalbos">
        <KalbuPasirinkimas
          className="prisijungimoKalbos"
          options={t('kalba')}
          optionsText={t('kalba_text')}
        />
      </div>
      
      <Box sx={{ p: 4, display: { xs: "block", sm: "none"} }}>
        <Typography
          sx={{
            textAlign: "center",
            py: 2,
            color: "common.white",
            fontWeight: 700,
            fontSize: "1.5em" 
          }}
        >
          {t('slaptazodzio_priminimas')}
        </Typography>
        <Typography
          sx={{
            color: "common.white",
            fontSize: "14px",
            fontWeight: 400
          }}
        >
          {step === 1 ? t('slaptazodzio_priminimas_txt') : t('otp_verification_desc') || 'Įveskite gautą 6 skaičių kodą'}
        </Typography>
      </Box>

      <div className="lentele">
        <div className="lenteleVidus">
          <Box sx={{ display: { xs: "none", sm: "block"} }}>
            <Typography
              sx={{
                textAlign: "center",
                py: 2,
                color: "primary.main",
                fontWeight: 700,
                fontSize: "1.5em" 
              }}
            >
              {t('slaptazodzio_priminimas')}
            </Typography>
            <Typography
              sx={{
                color: "common.black",
                fontSize: "14px",
                fontWeight: 400
              }}
            >
              {step === 1 ? t('slaptazodzio_priminimas_txt') : t('otp_verification_desc') || 'Įveskite gautą 6 skaičių kodą'}
            </Typography>
          </Box>
          
          {step === 1 ? (
            <form onSubmit={handleSendOTP}>
              <div>
                <PrimaryTextField
                  id="reset-password-email"
                  label="login_email"
                  onChange={(e) => { setEmail(e) }}
                  type="email"
                  fullWidth={true}
                  size="small"
                  classNameForm="txtFielfContainer reg"
                  validate="email"
                  value={email}
                  errorMsg={t('ivestkiteEmail')}
                  disabled={isLoading}
                />
              </div>
              
              <div className="submitButtonCont">
                <PrimaryButton
                  type="submit"
                  disabled={!isEmailValid || isLoading}
                  text={isLoading ? 'processing' : 'send_code'}
                  onClick={handleSendOTP}
                  className="mobLogIn"
                />
              </div>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP}>
              <div>
                <PrimaryTextField
                  id="otp-code"
                  label="verification_code"
                  onChange={(e) => { 
                    // Only allow numbers and limit to 6 digits
                    const value = e.replace(/\D/g, '').slice(0, 6);
                    setOtp(value);
                  }}
                  type="text"
                  fullWidth={true}
                  size="small"
                  classNameForm="txtFielfContainer reg"
                  value={otp}
                  errorMsg={t('enter_6_digit_code') || 'Įveskite 6 skaičių kodą'}
                  disabled={isLoading}
                  inputProps={{
                    maxLength: 6,
                    pattern: "[0-9]*",
                    inputMode: "numeric"
                  }}
                />
              </div>
              
              <div className="submitButtonCont">
                <PrimaryButton
                  type="submit"
                  disabled={otp.length !== 6 || isLoading}
                  text={isLoading ? 'processing' : 'verify_code'}
                  onClick={handleVerifyOTP}
                  className="mobLogIn"
                />
              </div>
              
              <div style={{ textAlign: 'center', marginTop: '15px' }}>
                {countdown > 0 ? (
                  <Typography variant="body2" color="textSecondary">
                    {t('resend_code_in') || 'Siųsti kodą iš naujo galėsite po'} {countdown}s
                  </Typography>
                ) : (
                  <Typography 
                    variant="body2" 
                    color="primary" 
                    style={{ cursor: 'pointer' }}
                    onClick={handleResendOTP}
                  >
                    {t('resend_code') || 'Siųsti kodą iš naujo'}
                  </Typography>
                )}
              </div>
            </form>
          )}
          
          <div className="gryztiIprisijungima">
            <span onClick={handleGoToLogin}>{t('grizti_i_prisijungima')}</span>
          </div>
        </div>
      </div>
      
      <a href="http://www.ivairove.lt" target="_blank" className="loginLogoCont" rel="noreferrer">
        <img className="loginLogo" src={Logo} alt="logo" />
      </a>
    </div>
  );
};

export default RequestPasswordResetOTP;